export type Language = 'en' | 'zh';

export interface LanguageOption {
    code: Language;
    name: string;
    nativeName: string;
}

export const languages: LanguageOption[] = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'zh', name: 'Chinese', nativeName: '汉语' },
];

export const translations: Record<Language, Record<string, string>> = {
    en: {
        // header
        appTitle: "Resonance",
        appSubtitle: "Coordinate time across the seven seas and resonate with your friends",

        // control panel
        currentTime: "Current Time",
        selectedTime: "Selected Time",
        language: "Language",

        // Settings Modal
        settings: "Settings",
        activeHours: "Active Hours",
        sleepHours: "Sleep Hours",
        start: "Start",
        end: "End",
        save: "Save",
        cancel: "Cancel",

        // Validation errors
        errorAllFieldsRequired: "All time values must be filled",
        errorInvalidRange: "All time values must be between 0 and 23",
        errorSameTime: "Start and end times cannot be the same",

        // City Search
        searchPlaceholder: "Search cities or regions...",
        searchNoResults: "No cities or regions found",

        // Resonance Slots
        resonanceSlotsTitle: "Resonance Slots",
        resonanceSlotsDescription: "Add timezones to see when everyone is available.",
        resonanceSlotsPerfectTime: "Perfect Collaboration Time",
        resonanceSlotsNoPerfectTime: "No perfect overlap found. Check partial availability below.",
        resonanceSlotsTimeline: "24-Hour Overview",
        resonanceSlotsAllActive: "All Active",
        resonanceSlotsSomeFree: "Some Free",
        resonanceSlotsSomeSleeping: "Some Sleeping",

        // Timezone List
        timezoneListPlaceholder: "No cities added yet.",
        timezoneListHint: "Use the search box to add cities to your list.",

        // Timezone Card
        timezoneCardActive: "Active",
        timezoneCardSleeping: "Sleeping",
        timezoneCardFree: "Free",

        // Relative dates and time differences
        dateTomorrow: "tomorrow",
        dateYesterday: "yesterday",
        dateInDays: "in {n} days",
        dateDaysAgo: "{n} days ago",
        diffSameTime: "Same time!",
        diffHours: "{n} h",
        diffMinutes: "{n} m",

        // Resonance Slots details
        resonanceSlotsHour: "(1 hour)",
        resonanceSlotsHours: "({n} hours)",
        resonanceSlotsActiveCount: "{active}/{total} active",
        resonanceSlotsFreeList: "Free: {cities}",
        resonanceSlotsSleepingList: "Sleeping: {cities}",
        resonanceSlotsClickToSet: "Click to set time",

        // City Search status
        searchAdded: "Added",

        // Accessibility labels
        ariaResumeLive: "Resume live time",
        ariaPauseLive: "Pause live time",
        ariaClearSearch: "Clear search",
        ariaRemoveCity: "Remove {city}",
        ariaViewOnGithub: "View on GitHub",
        searchLabel: "Search cities",
        timelineLabel: "Time in {city}",

        // Footer
        madeBy: "Made by Peylix",

        // Cities
        cityBeijing: "Beijing",
        cityShanghai: "Shanghai",
        cityGuangzhou: "Guangzhou",
        cityShenzhen: "Shenzhen",
        cityHongKong: "Hong Kong",
        cityMacau: "Macau",
        cityTaipei: "Taipei",
        cityTokyo: "Tokyo",
        cityOsaka: "Osaka",
        citySeoul: "Seoul",
        cityUlaanbaatar: "Ulaanbaatar",
        citySingapore: "Singapore",
        cityKualaLumpur: "Kuala Lumpur",
        cityBangkok: "Bangkok",
        cityHoChiMinh: "Ho Chi Minh City",
        cityHanoi: "Hanoi",
        cityJakarta: "Jakarta",
        cityBali: "Bali",
        cityManila: "Manila",
        cityYangon: "Yangon",
        cityPhnomPenh: "Phnom Penh",
        cityNewDelhi: "New Delhi",
        cityMumbai: "Mumbai",
        cityBangalore: "Bangalore",
        cityKarachi: "Karachi",
        cityDhaka: "Dhaka",
        cityColombo: "Colombo",
        cityKathmandu: "Kathmandu",
        cityKabul: "Kabul",
        cityAlmaty: "Almaty",
        cityTashkent: "Tashkent",
        cityDubai: "Dubai",
        cityAbuDhabi: "Abu Dhabi",
        cityRiyadh: "Riyadh",
        cityDoha: "Doha",
        cityTehran: "Tehran",
        cityBaghdad: "Baghdad",
        cityTelAviv: "Tel Aviv",
        cityIstanbul: "Istanbul",
        cityLondon: "London",
        cityDublin: "Dublin",
        cityLisbon: "Lisbon",
        cityReykjavik: "Reykjavik",
        cityParis: "Paris",
        cityBerlin: "Berlin",
        cityMunich: "Munich",
        cityAmsterdam: "Amsterdam",
        cityBrussels: "Brussels",
        cityMadrid: "Madrid",
        cityBarcelona: "Barcelona",
        cityRome: "Rome",
        cityMilan: "Milan",
        cityZurich: "Zurich",
        cityVienna: "Vienna",
        cityStockholm: "Stockholm",
        cityOslo: "Oslo",
        cityCopenhagen: "Copenhagen",
        cityHelsinki: "Helsinki",
        cityWarsaw: "Warsaw",
        cityPrague: "Prague",
        cityBudapest: "Budapest",
        cityAthens: "Athens",
        cityBucharest: "Bucharest",
        cityKyiv: "Kyiv",
        cityMoscow: "Moscow",
        cityVladivostok: "Vladivostok",
        cityCairo: "Cairo",
        cityCasablanca: "Casablanca",
        cityLagos: "Lagos",
        cityAccra: "Accra",
        cityNairobi: "Nairobi",
        cityAddisAbaba: "Addis Ababa",
        cityJohannesburg: "Johannesburg",
        cityCapeTown: "Cape Town",
        citySeattle: "Seattle",
        cityLosAngeles: "Los Angeles",
        citySanFrancisco: "San Francisco",
        cityDenver: "Denver",
        cityPhoenix: "Phoenix",
        cityChicago: "Chicago",
        cityHouston: "Houston",
        cityNewYork: "New York",
        cityWashington: "Washington, D.C.",
        cityBoston: "Boston",
        cityMiami: "Miami",
        cityAnchorage: "Anchorage",
        cityHonolulu: "Honolulu",
        cityVancouver: "Vancouver",
        cityCalgary: "Calgary",
        cityToronto: "Toronto",
        cityMontreal: "Montreal",
        cityHalifax: "Halifax",
        cityStJohns: "St. John's",
        cityMexicoCity: "Mexico City",
        cityBogota: "Bogotá",
        cityLima: "Lima",
        cityCaracas: "Caracas",
        citySantiago: "Santiago",
        cityBuenosAires: "Buenos Aires",
        citySaoPaulo: "São Paulo",
        cityRioDeJaneiro: "Rio de Janeiro",
        cityQueensland: "Queensland",
        citySydney: "Sydney",
        cityMelbourne: "Melbourne",
        cityAdelaide: "Adelaide",
        cityDarwin: "Darwin",
        cityPerth: "Perth",
        cityAuckland: "Auckland",
        citySuva: "Suva",
        cityUTC: "UTC",

        // Regions
        regionCN: "Mainland China",
        regionHK: "Hong Kong SAR",
        regionMO: "Macao SAR",
        regionTW: "Taiwan",
        regionJP: "Japan",
        regionKR: "South Korea",
        regionMN: "Mongolia",
        regionSG: "Singapore",
        regionMY: "Malaysia",
        regionTH: "Thailand",
        regionVN: "Vietnam",
        regionID: "Indonesia",
        regionPH: "Philippines",
        regionMM: "Myanmar",
        regionKH: "Cambodia",
        regionIN: "India",
        regionPK: "Pakistan",
        regionBD: "Bangladesh",
        regionLK: "Sri Lanka",
        regionNP: "Nepal",
        regionAF: "Afghanistan",
        regionKZ: "Kazakhstan",
        regionUZ: "Uzbekistan",
        regionAE: "United Arab Emirates",
        regionSA: "Saudi Arabia",
        regionQA: "Qatar",
        regionIR: "Iran",
        regionIQ: "Iraq",
        regionIL: "Israel",
        regionTR: "Türkiye",
        regionUK: "United Kingdom",
        regionIE: "Ireland",
        regionPT: "Portugal",
        regionIS: "Iceland",
        regionFR: "France",
        regionDE: "Germany",
        regionNL: "Netherlands",
        regionBE: "Belgium",
        regionES: "Spain",
        regionIT: "Italy",
        regionCH: "Switzerland",
        regionAT: "Austria",
        regionSE: "Sweden",
        regionNO: "Norway",
        regionDK: "Denmark",
        regionFI: "Finland",
        regionPL: "Poland",
        regionCZ: "Czechia",
        regionHU: "Hungary",
        regionGR: "Greece",
        regionRO: "Romania",
        regionUA: "Ukraine",
        regionRU: "Russia",
        regionEG: "Egypt",
        regionMA: "Morocco",
        regionNG: "Nigeria",
        regionGH: "Ghana",
        regionKE: "Kenya",
        regionET: "Ethiopia",
        regionZA: "South Africa",
        regionUS: "United States",
        regionCA: "Canada",
        regionMX: "Mexico",
        regionCO: "Colombia",
        regionPE: "Peru",
        regionVE: "Venezuela",
        regionCL: "Chile",
        regionAR: "Argentina",
        regionBR: "Brazil",
        regionAUS: "Australia",
        regionNZ: "New Zealand",
        regionFJ: "Fiji",
        regionUTC: "Coordinated Universal Time",
    },

    zh: {
        // header
        appTitle: "共鸣钟",
        appSubtitle: "即便远在大洋之外，我们依旧可以共鸣",

        // control panel
        currentTime: "当前时间",
        selectedTime: "选定时间",
        language: "语言",

        // Settings Modal
        settings: "设置",
        activeHours: "活跃时间",
        sleepHours: "睡眠时间",
        start: "开始",
        end: "结束",
        save: "保存",
        cancel: "取消",

        // Validation errors
        errorAllFieldsRequired: "请填写所有时间值",
        errorInvalidRange: "所有时间值必须在 0 到 23 之间",
        errorSameTime: "开始和结束时间不能相同",

        // City Search
        searchPlaceholder: "搜索城市或地区……",
        searchNoResults: "没有找到对应城市或地区",

        // Resonance Slots
        resonanceSlotsTitle: "共鸣时段",
        resonanceSlotsDescription: "添加时区以查看大家何时都在线。",
        resonanceSlotsPerfectTime: "完美协作时间",
        resonanceSlotsNoPerfectTime: "未找到完美重叠时间。请查看下方的部分可用时间。",
        resonanceSlotsTimeline: "24 小时概览",
        resonanceSlotsAllActive: "都处于活跃时间",
        resonanceSlotsSomeFree: "有人处于空闲时间",
        resonanceSlotsSomeSleeping: "有人处于睡眠时间",

        // Timezone List
        timezoneListPlaceholder: "尚未添加任何城市。",
        timezoneListHint: "使用搜索框可以把城市添加到列表中。",

        // Timezone Card
        timezoneCardActive: "活跃时间",
        timezoneCardSleeping: "睡觉时间",
        timezoneCardFree: "空闲时间",

        // Relative dates and time differences
        dateTomorrow: "明天",
        dateYesterday: "昨天",
        dateInDays: "{n} 天后",
        dateDaysAgo: "{n} 天前",
        diffSameTime: "时间相同！",
        diffHours: "{n} 小时",
        diffMinutes: "{n} 分钟",

        // Resonance Slots details
        resonanceSlotsHour: "（1 小时）",
        resonanceSlotsHours: "（{n} 小时）",
        resonanceSlotsActiveCount: "{active}/{total} 活跃",
        resonanceSlotsFreeList: "空闲：{cities}",
        resonanceSlotsSleepingList: "睡眠：{cities}",
        resonanceSlotsClickToSet: "点击设置时间",

        // City Search status
        searchAdded: "已添加",

        // Accessibility labels
        ariaResumeLive: "恢复实时时间",
        ariaPauseLive: "暂停实时时间",
        ariaClearSearch: "清除搜索",
        ariaRemoveCity: "移除{city}",
        ariaViewOnGithub: "在 GitHub 上查看",
        searchLabel: "搜索城市",
        timelineLabel: "{city}的时间",

        // Footer
        madeBy: "由 Peylix 制作",

        // Cities
        cityBeijing: "北京",
        cityShanghai: "上海",
        cityGuangzhou: "广州",
        cityShenzhen: "深圳",
        cityHongKong: "香港",
        cityMacau: "澳门",
        cityTaipei: "台北",
        cityTokyo: "东京",
        cityOsaka: "大阪",
        citySeoul: "首尔",
        cityUlaanbaatar: "乌兰巴托",
        citySingapore: "新加坡",
        cityKualaLumpur: "吉隆坡",
        cityBangkok: "曼谷",
        cityHoChiMinh: "胡志明市",
        cityHanoi: "河内",
        cityJakarta: "雅加达",
        cityBali: "巴厘岛",
        cityManila: "马尼拉",
        cityYangon: "仰光",
        cityPhnomPenh: "金边",
        cityNewDelhi: "新德里",
        cityMumbai: "孟买",
        cityBangalore: "班加罗尔",
        cityKarachi: "卡拉奇",
        cityDhaka: "达卡",
        cityColombo: "科伦坡",
        cityKathmandu: "加德满都",
        cityKabul: "喀布尔",
        cityAlmaty: "阿拉木图",
        cityTashkent: "塔什干",
        cityDubai: "迪拜",
        cityAbuDhabi: "阿布扎比",
        cityRiyadh: "利雅得",
        cityDoha: "多哈",
        cityTehran: "德黑兰",
        cityBaghdad: "巴格达",
        cityTelAviv: "特拉维夫",
        cityIstanbul: "伊斯坦布尔",
        cityLondon: "伦敦",
        cityDublin: "都柏林",
        cityLisbon: "里斯本",
        cityReykjavik: "雷克雅未克",
        cityParis: "巴黎",
        cityBerlin: "柏林",
        cityMunich: "慕尼黑",
        cityAmsterdam: "阿姆斯特丹",
        cityBrussels: "布鲁塞尔",
        cityMadrid: "马德里",
        cityBarcelona: "巴塞罗那",
        cityRome: "罗马",
        cityMilan: "米兰",
        cityZurich: "苏黎世",
        cityVienna: "维也纳",
        cityStockholm: "斯德哥尔摩",
        cityOslo: "奥斯陆",
        cityCopenhagen: "哥本哈根",
        cityHelsinki: "赫尔辛基",
        cityWarsaw: "华沙",
        cityPrague: "布拉格",
        cityBudapest: "布达佩斯",
        cityAthens: "雅典",
        cityBucharest: "布加勒斯特",
        cityKyiv: "基辅",
        cityMoscow: "莫斯科",
        cityVladivostok: "符拉迪沃斯托克",
        cityCairo: "开罗",
        cityCasablanca: "卡萨布兰卡",
        cityLagos: "拉各斯",
        cityAccra: "阿克拉",
        cityNairobi: "内罗毕",
        cityAddisAbaba: "亚的斯亚贝巴",
        cityJohannesburg: "约翰内斯堡",
        cityCapeTown: "开普敦",
        citySeattle: "西雅图",
        cityLosAngeles: "洛杉矶",
        citySanFrancisco: "旧金山",
        cityDenver: "丹佛",
        cityPhoenix: "菲尼克斯",
        cityChicago: "芝加哥",
        cityHouston: "休斯敦",
        cityNewYork: "纽约",
        cityWashington: "华盛顿",
        cityBoston: "波士顿",
        cityMiami: "迈阿密",
        cityAnchorage: "安克雷奇",
        cityHonolulu: "檀香山",
        cityVancouver: "温哥华",
        cityCalgary: "卡尔加里",
        cityToronto: "多伦多",
        cityMontreal: "蒙特利尔",
        cityHalifax: "哈利法克斯",
        cityStJohns: "圣约翰斯",
        cityMexicoCity: "墨西哥城",
        cityBogota: "波哥大",
        cityLima: "利马",
        cityCaracas: "加拉加斯",
        citySantiago: "圣地亚哥",
        cityBuenosAires: "布宜诺斯艾利斯",
        citySaoPaulo: "圣保罗",
        cityRioDeJaneiro: "里约热内卢",
        cityQueensland: "昆士兰",
        citySydney: "悉尼",
        cityMelbourne: "墨尔本",
        cityAdelaide: "阿德莱德",
        cityDarwin: "达尔文",
        cityPerth: "珀斯",
        cityAuckland: "奥克兰",
        citySuva: "苏瓦",
        cityUTC: "协调世界时",

        // Regions
        regionCN: "中国大陆",
        regionHK: "香港特别行政区",
        regionMO: "澳门特别行政区",
        regionTW: "台湾",
        regionJP: "日本",
        regionKR: "韩国",
        regionMN: "蒙古",
        regionSG: "新加坡",
        regionMY: "马来西亚",
        regionTH: "泰国",
        regionVN: "越南",
        regionID: "印度尼西亚",
        regionPH: "菲律宾",
        regionMM: "缅甸",
        regionKH: "柬埔寨",
        regionIN: "印度",
        regionPK: "巴基斯坦",
        regionBD: "孟加拉国",
        regionLK: "斯里兰卡",
        regionNP: "尼泊尔",
        regionAF: "阿富汗",
        regionKZ: "哈萨克斯坦",
        regionUZ: "乌兹别克斯坦",
        regionAE: "阿联酋",
        regionSA: "沙特阿拉伯",
        regionQA: "卡塔尔",
        regionIR: "伊朗",
        regionIQ: "伊拉克",
        regionIL: "以色列",
        regionTR: "土耳其",
        regionUK: "英国",
        regionIE: "爱尔兰",
        regionPT: "葡萄牙",
        regionIS: "冰岛",
        regionFR: "法国",
        regionDE: "德国",
        regionNL: "荷兰",
        regionBE: "比利时",
        regionES: "西班牙",
        regionIT: "意大利",
        regionCH: "瑞士",
        regionAT: "奥地利",
        regionSE: "瑞典",
        regionNO: "挪威",
        regionDK: "丹麦",
        regionFI: "芬兰",
        regionPL: "波兰",
        regionCZ: "捷克",
        regionHU: "匈牙利",
        regionGR: "希腊",
        regionRO: "罗马尼亚",
        regionUA: "乌克兰",
        regionRU: "俄罗斯",
        regionEG: "埃及",
        regionMA: "摩洛哥",
        regionNG: "尼日利亚",
        regionGH: "加纳",
        regionKE: "肯尼亚",
        regionET: "埃塞俄比亚",
        regionZA: "南非",
        regionUS: "美国",
        regionCA: "加拿大",
        regionMX: "墨西哥",
        regionCO: "哥伦比亚",
        regionPE: "秘鲁",
        regionVE: "委内瑞拉",
        regionCL: "智利",
        regionAR: "阿根廷",
        regionBR: "巴西",
        regionAUS: "澳大利亚",
        regionNZ: "新西兰",
        regionFJ: "斐济",
        regionUTC: "世界标准时间",
    },

} as const;

export type TranslationKey = keyof typeof translations.en;
